/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC } from "react"

import {
  InspectionDecision,
  ReturnRequestItem,
} from "ecommerce/constants/returnRefund"

import { ReturnInspectionItemCard } from "./ReturnInspectionItemCard"
import styles from "./ReturnInspectionPanel.modules.css"

interface ReturnInspectionPanelProps {
  items: ReturnRequestItem[]
  /**
   * Controlled by the drawer, which owns the decisions because `Confirm` lives in its
   * footer rather than in this panel.
   */
  decisions: Record<string, InspectionDecision>
  onChange(
    itemID: string,
    update: (previous?: InspectionDecision) => InspectionDecision,
  ): void
  /** Stages one added photo as a server-side draft — see the card's prop. */
  onUploadPhoto(
    item: ReturnRequestItem,
    file: File,
    mediaSource?: string,
  ): Promise<string>
  /** Deletes one server-staged draft photo — see the card's prop. */
  onRemovePhoto(item: ReturnRequestItem, assetID: string): Promise<boolean>
}

/**
 * Decision 2 — inspecting the returned goods. A sibling of `ReturnReviewPanel`, not a
 * parametrisation of it: the two decisions carry disjoint action sets and reason lists
 * and evolve independently (the tickets are explicit that the implementations must not
 * be shared).
 */
export const ReturnInspectionPanel: FC<ReturnInspectionPanelProps> = ({
  items,
  decisions,
  onChange,
  onUploadPhoto,
  onRemovePhoto,
}) => (
  <div className={styles.panel}>
    {items.map(item => (
      <ReturnInspectionItemCard
        key={item.id}
        item={item}
        decision={decisions[item.id]}
        onChange={onChange}
        onUploadPhoto={onUploadPhoto}
        onRemovePhoto={onRemovePhoto}
      />
    ))}
  </div>
)
